import { useEffect, useRef, useState } from "react";
import CommonModal from "../../ResetContext/components/DumpModal";
import URLType from "./URLType";
import SingleWebpage from "./SingleWebpage";
import { useUserInfo } from "../../../../../context/user";
import toast, { Toaster } from "react-hot-toast";
import { isValidUrl } from "./urlValidation";
import {
  useStartScrapeJob,
  useScrapeJobStatus,
  useScrapedUrls,
  useUploadScrapedWebsite,
} from "../../../../../queries/scraping";

const NewAddWebsiteModal = ({
  isOpen,
  onClose,
  getuploadfiles,
  isSharedKnowledge = false,
}) => {
  const [urlType, setUrlType] = useState("");
  const [url, setUrl] = useState("");
  const [selectedOption, setSelectedOption] = useState([]);
  const [userInfo, , , , userActiveLocation] = useUserInfo();
  const submitUrlsRef = useRef(false);
  const [crawlerModal, setCrawlerModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [jobId, setJobId] = useState("");
  const [urls, setUrls] = useState([]);

  const profileId = userActiveLocation?.id;

  const handleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((sid) => sid !== id)
        : prev.length < 10
          ? [...prev, id]
          : prev
    );
  };

  const startScrapeJob = useStartScrapeJob();
  const statusQuery = useScrapeJobStatus(jobId, profileId, {
    stopPolling: () => submitUrlsRef.current,
  });
  const jobStatus = statusQuery.data?.data?.status;
  const isDone = !!jobId && !!jobStatus && jobStatus !== "in_progress";
  const finalUrlsQuery = useScrapedUrls(jobId, profileId, isDone);
  const uploadWebsite = useUploadScrapedWebsite();

  // As each status poll comes back, fold its urls in (deduped) — same
  // progressive-accumulation behavior as the old recursive pollJobStatus.
  useEffect(() => {
    if (!statusQuery.data) return;
    setCrawlerModal(true);
    setUrlType("webpage");
    if (statusQuery.data?.urls?.length > 0) {
      setUrls((prev) => [...new Set([...prev, ...statusQuery.data.urls])]);
    }
  }, [statusQuery.data]);

  // Once the job leaves "in_progress", the final urls fetch runs once and
  // its results get appended (matching the original fetchScrapedUrls, which
  // appended onto whatever the polling had already gathered).
  useEffect(() => {
    if (finalUrlsQuery.data?.data?.length > 0) {
      setUrls((prev) => [...prev, ...finalUrlsQuery.data.data]);
    }
  }, [finalUrlsQuery.data]);

  const loading =
    startScrapeJob.isPending || (!!jobId && (!isDone || finalUrlsQuery.isFetching));

  const scrapedData = {
    totalUrls: 0,
    urls,
    jobId,
    loading: uploadWebsite.isPending,
  };

  const scrapeWebsite = async () => {
    if (!isValidUrl(url)) {
      toast.error("Please enter a valid website URL");
      return;
    }

    if (!isSharedKnowledge && selectedOption.length === 0) {
      toast.error("Please select at least one assistant");
      return;
    }

    try {
      const data = await startScrapeJob.mutateAsync({ url, profileId });
      if (data?.jobId) {
        setJobId(data.jobId);
      } else {
        toast.error("Failed to initiate scraping. Please try again.");
      }
    } catch (error) {
      console.error("Error initiating scraping:", error);
    }
  };

  const scrapedSubmitHandler = async () => {
    if (!isSharedKnowledge && (!selectedOption || selectedOption.length === 0)) {
      toast.error("Please select at least one assistant.");
      return;
    }

    const filteredUrls = selected.filter((u) => u != null);
    const payload = isSharedKnowledge
      ? {
          job_id: jobId,
          urls: filteredUrls,
          username: userInfo?.userName,
        }
      : {
          assistant_ids: selectedOption?.map((item) => item.value),
          profile_id: profileId,
          website: filteredUrls,
          username: userInfo?.userName,
          jobId,
        };

    try {
      const response = await uploadWebsite.mutateAsync({ isSharedKnowledge, profileId, payload });
      submitUrlsRef.current = true;
      getuploadfiles();
      onClose(false);
      toast.success(response?.message || "Webpages added successfully!");
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        "There was an error uploading the website. Please try again."
      );
    }
  };

  const singleUrlSubmit = async () => {
    // Validate URL using the `validator` package (same rule used for scraping)
    if (!isValidUrl(url)) {
      toast.error("Please enter a valid URL");
      return;
    }
    if (!isSharedKnowledge && (!selectedOption || selectedOption.length === 0)) {
      toast.error("Please select at least one assistant.");
      return;
    }

    const payload = isSharedKnowledge
      ? {
          webpage_url: url.trim(),
          username: userInfo?.userName,
        }
      : {
          assistant_ids: selectedOption?.map((item) => item.value),
          profile_id: profileId,
          website: url,
          username: userInfo?.userName,
        };

    try {
      const response = await uploadWebsite.mutateAsync({ isSharedKnowledge, profileId, payload });
      submitUrlsRef.current = true;
      getuploadfiles();
      onClose(false);
      toast.success(response?.message || "Webpages added successfully!");
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        "There was an error uploading the website. Please try again."
      );
    }
  };

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        urlType === ""
          ? "Add a URL"
          : urlType === "single"
            ? "Add a Webpage"
            : urlType === "multi"
              ? "Add a Website"
              : "Select Webpages"
      }
      showAlert={false}
    >
      {urlType === "" && <URLType setUrlType={setUrlType} />}
      {(urlType === "single" ||
        urlType === "multi" ||
        urlType === "webpage") && (
          <SingleWebpage
            urlType={urlType}
            isSharedKnowledge={isSharedKnowledge}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            url={url}
            setUrl={setUrl}
            scrapedData={scrapedData}
            loading={loading}
            scrapeWebsite={scrapeWebsite}
            handleSelect={handleSelect}
            selected={selected}
            crawlerModal={crawlerModal}
            submitHandler={
              urlType === "single" ? singleUrlSubmit : scrapedSubmitHandler
            }
          />
        )}
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          style: { zIndex: 2147483647 },
        }}
      />
    </CommonModal>
  );
};

export default NewAddWebsiteModal;
